import { useEffect } from 'react';
import classnames from 'classnames';

import { Game } from '~/types/game';

import styles from './FreePlayTransition.module.css';

/** Per-language micro-copy for the transition. The overline ("next game") and the
    get-ready prompt are shown as a bilingual pair so a traveller in either language
    is addressed — the TV is shared. Add a locale to support a new airport. */
const CONTENT: Record<string, { over: string; ready: string }> = {
    fr: { over: 'Prochain jeu', ready: 'Préparez-vous…' },
    en: { over: 'Next game', ready: 'Get ready…' },
    es: { over: 'Próximo juego', ready: 'Preparados…' },
    de: { over: 'Nächstes Spiel', ready: 'Macht euch bereit…' },
    zh: { over: '下一个游戏', ready: '准备好…' },
};

export type FreePlayTransitionProps = {
    /** The game being launched (metadata may resolve a tick after the switch). */
    game?: Game;
    /** Fallback label while `game` metadata is still loading. */
    gameID: string;
    /** True once the new game's iframe has finished its handshake → play the exit. */
    ready: boolean;
    /** Display language pair (TV is shared). Defaults to FR/EN. */
    languages?: string[];
    /** Called after the exit animation completes, so the parent can unmount us. */
    onDone: () => void;
};

/**
 * Free-play game-switch transition — a full-screen "now launching" curtain shown on
 * the TV over the game iframe while the next game loads. It covers the brief iframe
 * reload (no blank/flashing frame, see bug #8b) and fades out at the EXACT moment the
 * new game signals ready (`ready` prop, driven by the iframe handshake) rather than on
 * a blind timer. Bilingual, cohesive with the attract screen (redesign tokens).
 *
 * Gated by the caller to free-play only → bars/anim are untouched.
 */
export function FreePlayTransition({ game, gameID, ready, languages = ['fr', 'en'], onDone }: FreePlayTransitionProps) {
    const pair = languages.filter((l) => CONTENT[l]);
    const langs = pair.length > 0 ? pair : ['fr', 'en'];

    const name = game?.name ?? gameID;
    const cover = game?.cover;

    // Safety net: if the exit animation's `animationend` never fires (e.g. reduced-motion
    // collapses the animation), still unmount shortly after `ready`.
    useEffect(() => {
        if (!ready) return undefined;
        const t = window.setTimeout(onDone, 900);
        return () => window.clearTimeout(t);
    }, [ready, onDone]);

    return (
        <div
            className={classnames(styles.stage, ready && styles.out)}
            role="status"
            aria-live="polite"
            onAnimationEnd={(e) => {
                // Only the stage's own exit animation (not a bubbled child entrance),
                // and only once we're actually exiting.
                if (ready && e.target === e.currentTarget) onDone();
            }}
        >
            {/* Atmospheric backdrop: the cover, blown up and blurred, lit from within. */}
            {cover && (
                <div className={styles.backdrop} style={{ backgroundImage: `url(${cover})` }} aria-hidden="true" />
            )}
            <div className={styles.aurora} aria-hidden="true" />
            <div className={styles.veil} aria-hidden="true" />
            <div className={styles.grain} aria-hidden="true" />

            <div className={styles.center}>
                {/* The game poster — the centrepiece that "arrives". */}
                <div className={styles.posterWrap}>
                    <div className={styles.posterGlow} aria-hidden="true" />
                    {cover ? (
                        <div className={styles.poster} style={{ backgroundImage: `url(${cover})` }} />
                    ) : (
                        <div className={classnames(styles.poster, styles.posterFallback)}>
                            <span>{name.charAt(0).toUpperCase()}</span>
                        </div>
                    )}
                    <div className={styles.posterSheen} aria-hidden="true" />
                </div>

                <div className={styles.copy}>
                    <div className={styles.overline}>
                        {langs.map((l, i) => (
                            <span key={l}>
                                {i > 0 && <span className={styles.dot}>·</span>}
                                {CONTENT[l].over}
                            </span>
                        ))}
                    </div>

                    <h1 className={styles.name}>{name}</h1>

                    <div className={styles.ready}>
                        <span className={styles.readyPrimary}>{CONTENT[langs[0]].ready}</span>
                        {langs[1] && <span className={styles.readySecondary}>{CONTENT[langs[1]].ready}</span>}
                    </div>
                </div>

                {/* Indeterminate loading sweep — honest "loading" without a fake %. */}
                <div className={styles.loader} aria-hidden="true">
                    <span className={styles.loaderBar} />
                </div>
            </div>
        </div>
    );
}
